/**
 * Loads Yandex Maps JS API v3 once and returns the global `ymaps3`.
 * Script is injected dynamically so `onload`/`onerror` run reliably (Create React App bundle order).
 *
 * Ключ: `REACT_APP_YANDEX_MAPS_API_KEY` в `.env` переопределяет значение по умолчанию.
 * Ответ `403` / `Invalid api key` от api-maps.yandex.ru — перевыпустите ключ в developer.tech.yandex.ru (JavaScript API Карт v3, HTTP Referer).
 *
 * @see https://yandex.ru/maps-api/docs/js-api/common/connection/javascript.html
 */

const SCRIPT_ID = 'yandex-maps-js-api-v3';

/** Значение по умолчанию; переопределение через REACT_APP_YANDEX_MAPS_API_KEY */
const DEFAULT_YANDEX_MAPS_API_KEY = 'b391e6d5-1852-48d7-b3c7-882830418afc';

function getApiKey() {
	const fromEnv = process.env.REACT_APP_YANDEX_MAPS_API_KEY;
	if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
	return DEFAULT_YANDEX_MAPS_API_KEY;
}

function waitForGlobalYmaps3(timeoutMs = 120000) {
	return new Promise((resolve, reject) => {
		const start = Date.now();
		const id = setInterval(() => {
			if (typeof window !== 'undefined' && window.ymaps3) {
				clearInterval(id);
				resolve(window.ymaps3);
			} else if (Date.now() - start > timeoutMs) {
				clearInterval(id);
				reject(
					new Error(
						'Яндекс.Карты: после загрузки скрипта объект ymaps3 не появился. Если запрос вернул Invalid api key (403), выпустите ключ для JavaScript API Карт v3 в developer.tech.yandex.ru и задайте HTTP Referer (в т.ч. localhost).'
					)
				);
			}
		}, 50);
	});
}

/**
 * @returns {Promise<typeof window.ymaps3>}
 */
export function loadYandexMapsApi() {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('Карта доступна только в браузере.'));
	}

	const key = getApiKey();

	if (window.ymaps3) {
		return Promise.resolve(window.ymaps3);
	}

	const existing = document.getElementById(SCRIPT_ID);
	if (existing) {
		return waitForGlobalYmaps3();
	}

	const url = `https://api-maps.yandex.ru/v3/?apikey=${encodeURIComponent(key)}&lang=ru_RU`;

	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.async = true;
		script.src = url;
		script.type = 'text/javascript';

		script.onerror = () => {
			reject(
				new Error(
					'Не удалось загрузить скрипт api-maps.yandex.ru. При 403 Invalid api key замените ключ в кабинете Яндекса или в REACT_APP_YANDEX_MAPS_API_KEY.'
				)
			);
		};

		script.onload = () => {
			waitForGlobalYmaps3(90000).then(resolve).catch(reject);
		};

		document.head.appendChild(script);
	});
}
